import { Card, List, MenuItem, Popper } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { MutableRefObject } from 'react';
import { tenantMeilisearchSearchKeyKeys } from 'src/api/query-keys/tenant/meilisearch-search-key';
import { useAvailableTenants } from 'src/hooks/api/use-available-tenants';
import { useUserStore } from 'src/store/user-store';
import { UserRole } from 'src/types/enum/user-role';

interface Props {
  anchorRef: MutableRefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  onMouseLeave?: () => void;
  onMouseEnter?: () => void;
  placement: 'bottom-end' | 'right-start';
}

export const AvailableTenantsPopper = ({
  anchorRef,
  open,
  onClose,
  onMouseLeave,
  onMouseEnter,
  placement,
}: Props) => {
  const setViewContext = useUserStore(state => state.setViewContext);
  const setIsChangingDirectory = useUserStore(
    state => state.setIsChangingDirectory,
  );
  const setDirectoryToken = useUserStore(state => state.setDirectoryToken);
  const setAdminToken = useUserStore(state => state.setAdminToken);
  const setRole = useUserStore(state => state.setRole);
  const setIsSSO = useUserStore(state => state.setIsSSO);

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { data: availableTenants } = useAvailableTenants();

  const handleChangeContext = (
    directoryToken: string,
    adminToken: string,
    role: UserRole,
    isSSO: boolean,
  ) => {
    setViewContext('tenant');
    setIsChangingDirectory(true);
    setDirectoryToken(directoryToken);
    setAdminToken(adminToken);
    setRole(role);
    setIsSSO(isSSO);

    onClose();

    queryClient.invalidateQueries({
      queryKey: tenantMeilisearchSearchKeyKeys.details(),
    });

    navigate({
      to: '/',
    });
  };

  return (
    <Popper
      anchorEl={anchorRef.current}
      open={open}
      placement={placement}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      sx={{ margin: placement === 'bottom-end' ? '10px 0 !important' : '' }}>
      <Card variant="outlined">
        <List>
          {availableTenants?.resources?.map(tenant => (
            <MenuItem
              key={tenant.token}
              onClick={() => {
                handleChangeContext(
                  tenant.token,
                  tenant.tenantAdminToken,
                  tenant.role,
                  tenant?.isSSO ?? false,
                );
              }}>
              {tenant.name}
            </MenuItem>
          ))}
        </List>
      </Card>
    </Popper>
  );
};
